import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MessageSquare } from "lucide-react";
import z from "zod"; import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { User } from "lucide-react";
import Image from "next/image";
import { MessageSquareText } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { X } from "lucide-react";
import { useState } from "react";
import { showToast } from "@/lib/toast";
import { handleApiErrors } from "@/lib/api-error-utils";
import { Loader2 } from "lucide-react";

const FormSchema = z.object({
  feedback: z.string().min(20, {
    message: "Feedback must be at least 20 characters",
  }),
})

export default function FeedbackDialog() {

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(false);

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      email: "",
      feedback: "",
    },
  })


  const onSubmit = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/feedback`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      const data = await response.json();

      if (response.ok) {
        form.reset();
        setOpen(false)
        showToast(data?.message || "Success", "success");
      } else {
        if (response.status === 400 || response.status === 409) {
          handleApiErrors(form, data.errors);
          return;
        }
        showToast(data?.message || "An error occurred during signin", "error");
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || "An error occurred during signin", "error");
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger onClick={() => setOpen(true)} asChild>
        <Button variant="default" className="rounded-full mb-2 cursor-pointer text-xs px-1 sm:text-[15px]">
          <MessageSquare />
          Give Feedback
        </Button>
      </PopoverTrigger>

      <PopoverContent className="me-4 w-[380px] shadow-none bg-transparent border-0 ">
        <Card className={`bg-white rounded-lg shadow-2xl border border-gray-200 gap-0 py-4 `}>

          <CardHeader className={`pb-3 flex items-center justify-between`} >
            <CardTitle className={`flex text-[#053B9D]`}>
              <MessageSquare className="size-5 mr-2" />Share Your Thoughts
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <Separator className={`p-0 m-0`} />
          <div className="px-5 pt-8">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                <FormField
                  control={form.control}
                  name="feedback"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <Label htmlFor="email" className="sr-only">
                        Feedback
                      </Label>
                      <div className="relative">
                        <FormControl>
                          <Textarea
                            placeholder="Type your message here."
                            {...field}
                            className="min-h-28 focus:ring-2 focus:ring-blue-500 focus:border-blue-500  h-12 bg-gray-50 border-gray-200 text-[#776F69] rounded-xl" />
                        </FormControl>
                      </div>
                      <FormMessage className="text-sm text-red-600" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <Label htmlFor="email" className="sr-only">
                        Name
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#776F69]" />
                        <FormControl>
                          <Input
                            id="name"
                            type="text"
                            placeholder="Your Name"
                            className="pl-10 h-12 bg-gray-50 border-gray-200 text-[#776F69] rounded-xl"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage className="text-sm text-red-600" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <Label htmlFor="email" className="sr-only">
                        Your Email
                      </Label>
                      <div className="relative">
                        <Image
                          src="/assets/icons/email.png"
                          width={20}
                          height={20}
                          alt="email"
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5"
                        />
                        <FormControl>
                          <Input
                            id="email"
                            type="email"
                            placeholder="Your Email "
                            className="pl-10 h-12 bg-gray-50 border-gray-200 text-[#776F69] rounded-xl"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage className="text-sm text-red-600" />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button type="submit" variant="default" className="rounded-full cursor-pointer">
                    {form.formState.isSubmitting ? (
                      <>
                        Sending...
                        <Loader2 className="ml-2 size-6 animate-spin" />
                      </>
                    ) : (
                      'Send Feedback'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </Card>
      </PopoverContent>
    </Popover>
  )
}